import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { type PDFDocumentProxy } from 'pdfjs-dist'

import { type FileAuthResponseData, useFileAuthMutation, usePostBinaryMutation } from '@core/app/api/filePreviewApi'
import { useFilePreview } from '@core/utils/contexts/FilePreviewContext/FilePreviewContext'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'
import { usePrintBlob } from '@core/utils/hooks/usePrint'

const baseClass = 'FilePreviewWindow'

function FilePdfPreview() {
    const { getTitle } = useApplyV2NavTranslations()
    const { previewFile, isOpen, closePreview, getFileName, fileExtension, folder } = useFilePreview()
    const [pdfDoc, setPdfDoc] = useState<PDFDocumentProxy | null>(null)
    const [pdfBlob, setPdfBlob] = useState<Blob | undefined>(undefined)
    const [url, setUrl] = useState<string | null>(null)
    const [downloadBlob, setDownloadBlob] = useState<Blob | null>(null)
    const [downloadUrl, setDownloadUrl] = useState<string | null>(null)
    const [totalPages, setTotalPages] = useState(0)
    const [currentPage, setCurrentPage] = useState(1)
    const [isPdfLoading, setIsPdfLoading] = useState(false)
    const [isFitToScreen, setIsFitToScreen] = useState(false)
    const canvasRefs = useRef<(HTMLCanvasElement | null)[]>([])
    const pageRefs = useRef<(HTMLDivElement | null)[]>([])
    const containerRef = useRef<HTMLDivElement>(null)
    const [zoom, setZoom] = useState(100)
    const [authRequest, { isLoading: isAuthLoading }] = useFileAuthMutation()
    const [testBinaryRequest, { isLoading: isBinaryLoading }] = usePostBinaryMutation()
    const [isScrolling, setIsScrolling] = useState(false)
    const scrollTimeout = useRef<NodeJS.Timeout>()
    const outputScale = useMemo(() => window.devicePixelRatio || 1, [])
    const renderedPages = useRef<Set<number>>(new Set())
    const renderingPages = useRef<Set<number>>(new Set())
    const [forceUpdate, setForceUpdate] = useState(0)
    const { loading, handlePrint } = usePrintBlob(pdfBlob)
    const [authData, setAuthData] = useState<FileAuthResponseData | null>(null)
    const [prevOverflow, setPrevOverflow] = useState<string | null>(document.body.style.overflow)

    useEffect(() => {
        if (!previewFile || !('Ext' in previewFile)) return
        const loadPdf = async () => {
            const isIdOrUid = 'CandidateFileID' in previewFile || 'fileUid' in previewFile
            if (
                !isOpen ||
                !previewFile ||
                !isIdOrUid ||
                !('Ext' in previewFile)
                // || previewFile.Ext !== 'pdf'
            ) {
                return
            }

            try {
                setIsPdfLoading(true)

                renderedPages.current.clear()
                renderingPages.current.clear()

                let tempApplyFileData = {
                    folder: folder ?? 'candidate_course',
                    fileId: previewFile.CandidateFileID,
                }

                // console.log(
                //     previewFile,
                //     'CandidateFileID' in previewFile,
                //     !previewFile.CandidateFileID,
                //     'fileUid' in previewFile,
                //     'uid' in previewFile
                // )

                if (
                    'CandidateFileID' in previewFile &&
                    !previewFile.CandidateFileID &&
                    'fileUid' in previewFile &&
                    'uid' in previewFile &&
                    folder === 'tmp-apply-file'
                ) {
                    tempApplyFileData = {
                        uid: previewFile.uid,
                        fileUid: previewFile.fileUid,
                        type: previewFile.type,
                        folder: 'tmp-apply-file',
                    }
                }

                const res = await authRequest(tempApplyFileData)
                if ('data' in res) {
                    console.log(res)
                    setAuthData(res.data)
                } else {
                    return
                }

                // const base64Data = await testBinaryRequest({
                //     folder: folder ?? 'candidate_course',
                //     fileId: previewFile.CandidateFileID,
                // }).unwrap()

                let bimaryPayload = {
                    folder: folder ?? 'candidate_course',
                    fileId: previewFile.CandidateFileID,
                }

                if (
                    'CandidateFileID' in previewFile &&
                    !previewFile.CandidateFileID &&
                    'fileUid' in previewFile &&
                    'uid' in previewFile &&
                    folder === 'tmp-apply-file'
                ) {
                    bimaryPayload = {
                        uid: previewFile.uid,
                        type: previewFile.type,
                        folder: res?.data?.folder,
                        fileUid: res?.data?.fileId,
                    }
                }

                const base64Data = await testBinaryRequest(bimaryPayload).unwrap()

                const pdfjs = await import('pdfjs-dist/webpack.mjs')

                const binaryString = atob(base64Data)
                const bytes = new Uint8Array(binaryString.length)
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i)
                }
                const pdfBlob = new Blob([bytes.buffer], { type: 'application/pdf' })
                setPdfBlob(pdfBlob)
                setDownloadBlob(pdfBlob)
                const objectUrl = URL.createObjectURL(pdfBlob)
                setDownloadUrl(objectUrl)
                const loadingTask = pdfjs.getDocument({ data: bytes.buffer })
                const pdf = await loadingTask.promise

                setPdfDoc(pdf)
                setTotalPages(pdf.numPages)
                setCurrentPage(1)
                canvasRefs.current = new Array(pdf.numPages).fill(null)
                pageRefs.current = new Array(pdf.numPages).fill(null)
            } catch (e) {
                console.error('Error PDF:', e)
            } finally {
                setIsPdfLoading(false)
            }
        }

        loadPdf()

        return () => {
            if (pdfDoc) {
                pdfDoc.destroy()
                setPdfDoc(null)
            }
        }
    }, [isOpen, previewFile, testBinaryRequest])

    const renderPage = useCallback(
        async (pageNum: number) => {
            if (renderingPages.current.has(pageNum)) {
                return
            }

            const canvas = canvasRefs.current[pageNum - 1]
            if (!pdfDoc || !canvas) return

            try {
                renderingPages.current.add(pageNum)

                const page = await pdfDoc.getPage(pageNum)

                const scale = zoom / 100 // TODO ---
                // const scale = (zoom / 100) * 2
                const initialViewport = page.getViewport({ scale: 1 })

                const containerWidth = containerRef?.current?.getBoundingClientRect().width || initialViewport.width
                const fitScale = containerWidth / initialViewport.width
                const finalScale = isFitToScreen ? fitScale : scale

                const viewport = page.getViewport({ scale: finalScale })

                // canvas.width = viewport.width
                // canvas.height = viewport.height
                canvas.width = Math.floor(viewport.width * outputScale)
                canvas.height = Math.floor(viewport.height * outputScale)
                canvas.style.width = `${Math.floor(viewport.width)}px`
                canvas.style.height = `${Math.floor(viewport.height)}px`

                const context = canvas.getContext('2d', {
                    alpha: false,
                    willReadFrequently: false,
                })

                if (!context) return

                context.fillStyle = '#ffffff'
                context.fillRect(0, 0, canvas.width, canvas.height)
                context.clearRect(0, 0, canvas.width, canvas.height)

                const renderTask = page.render({
                    canvasContext: context,
                    viewport: viewport,
                    background: 'white',
                    transform: outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : undefined,
                    canvas,
                })

                await renderTask.promise

                renderedPages.current.add(pageNum)
                setForceUpdate((prev) => prev + 1)
            } catch (e) {
                console.error(`Render page error ${pageNum}:`, e)
            } finally {
                renderingPages.current.delete(pageNum)
            }
        },
        [pdfDoc, zoom]
    )

    const updateCurrentPage = useCallback(() => {
        if (!containerRef.current || !pageRefs.current.length) return

        setIsScrolling(true)

        if (scrollTimeout.current) {
            clearTimeout(scrollTimeout.current)
        }

        scrollTimeout.current = setTimeout(() => {
            setIsScrolling(false)
        }, 150)

        const container = containerRef.current
        const containerTop = container.scrollTop
        const containerBottom = containerTop + container.clientHeight

        let maxVisiblePage = 1
        let maxVisibleArea = 0

        pageRefs.current.forEach((pageRef, index) => {
            if (!pageRef) return

            const pageTop = pageRef.offsetTop
            const pageBottom = pageTop + pageRef.offsetHeight

            const visibleTop = Math.max(pageTop, containerTop)
            const visibleBottom = Math.min(pageBottom, containerBottom)
            const visibleHeight = Math.max(0, visibleBottom - visibleTop)

            if (visibleHeight > maxVisibleArea) {
                maxVisibleArea = visibleHeight
                maxVisiblePage = index + 1
            }
        })
        setCurrentPage(maxVisiblePage)
    }, [containerRef, pageRefs])

    useEffect(() => {
        const container = containerRef.current
        if (!container) return

        container.addEventListener('scroll', updateCurrentPage)
        window.addEventListener('resize', updateCurrentPage)

        setTimeout(updateCurrentPage, 500)

        return () => {
            container.removeEventListener('scroll', updateCurrentPage)
            window.removeEventListener('resize', updateCurrentPage)
            if (scrollTimeout.current) {
                clearTimeout(scrollTimeout.current)
            }
        }
    }, [])

    useEffect(() => {
        if (!pdfDoc) return

        const renderAllPages = async () => {
            const renderPromises = []
            for (let i = 1; i <= pdfDoc.numPages; i++) {
                if (!renderedPages.current.has(i)) {
                    renderPromises.push(new Promise((resolve) => setTimeout(resolve, 50)).then(() => renderPage(i)))
                }
            }
            await Promise.all(renderPromises)

            updateCurrentPage()
        }

        renderAllPages()
    }, [pdfDoc, renderPage, updateCurrentPage])

    useEffect(() => {
        if (!pdfDoc) return

        const rerenderAllPages = async () => {
            canvasRefs.current.forEach((canvas, index) => {
                if (canvas) {
                    const context = canvas.getContext('2d')
                    if (context) {
                        context.clearRect(0, 0, canvas.width, canvas.height)
                    }
                }
            })

            renderedPages.current.clear()
            renderingPages.current.clear()

            await new Promise((resolve) => setTimeout(resolve, 100))

            for (let i = 1; i <= pdfDoc.numPages; i++) {
                await renderPage(i)
            }

            updateCurrentPage()
        }

        rerenderAllPages()
    }, [zoom, pdfDoc, renderPage, updateCurrentPage])

    useEffect(() => {
        if (!containerRef.current || !pdfDoc) return

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const canvas = entry.target as HTMLCanvasElement
                        const pageNum = parseInt(canvas.dataset.pageNum || '0')

                        if (pageNum && !renderedPages.current.has(pageNum) && !renderingPages.current.has(pageNum)) {
                            renderPage(pageNum)
                        }
                    }
                })
            },
            { threshold: 0.1, rootMargin: '200px' }
        )

        canvasRefs.current.forEach((canvas) => {
            if (canvas) observer.observe(canvas)
        })

        return () => observer.disconnect()
    }, [pdfDoc, renderPage])

    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                closePreview()
            }
            const isModifierPressed = e.ctrlKey || e.metaKey

            if (isModifierPressed) {
                if (e.key === '=' || e.key === '+') {
                    e.preventDefault()
                    handleZoomChange('increase')
                }

                if (e.key === '-' || e.key === '_') {
                    e.preventDefault()
                    handleZoomChange('decrease')
                }

                if (e.key === '0') {
                    e.preventDefault()
                    setZoom(100)
                }
            }
        },
        [closePreview]
    )

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown)
            document.body.style.overflow = 'hidden'
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown)
            // document.body.style.overflow = 'unset'
            document.body.style.overflow = prevOverflow || 'unset'
        }
    }, [isOpen, handleKeyDown])

    const handleZoomChange = (value: 'decrease' | 'increase') => {
        if (value === 'decrease') {
            setZoom((prev) => {
                if (prev > 200) {
                    setIsFitToScreen(false)
                    return 200
                }
                return Math.max(25, prev - 25)
            })
        } else {
            setZoom((prev) => Math.min(200, prev + 25))
        }
    }

    const scrollToPage = (pageNum: number) => {
        const pageRef = pageRefs.current[pageNum - 1]
        if (pageRef && containerRef.current) {
            pageRef.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
    }

    const handlePrevPage = () => {
        if (currentPage > 1) {
            scrollToPage(currentPage - 1)
        }
    }

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            scrollToPage(currentPage + 1)
        }
    }

    // const lastModified = (() => {
    //     if (previewFile && 'lastModified' in previewFile) {
    //         return previewFile.lastModified || 0
    //     } else if (previewFile && 'uploaded' in previewFile) {
    //         const uploaded = Date.parse(previewFile.uploaded ?? '')
    //         return isNaN(uploaded) ? 0 : uploaded
    //     }
    //     return 0
    // })()
    //
    // const formattedDate = lastModified
    //     ? new Date(lastModified).toLocaleDateString('en-GB', {
    //           day: '2-digit',
    //           month: 'short',
    //           year: 'numeric',
    //       })
    //     : ''

    const handleDownload = useCallback(() => {
        if (downloadBlob) {
            try {
                const blobUrl = URL.createObjectURL(downloadBlob)

                const link = document.createElement('a')
                link.href = blobUrl
                link.download = getFileName() || `document.${fileExtension || 'pdf'}`
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)

                setTimeout(() => {
                    URL.revokeObjectURL(blobUrl)
                }, 100)

                console.log('File downloaded successfully')
            } catch (error) {
                console.error('Error downloading file:', error)
            }
        } else if (url) {
            fetch(url, {
                method: 'GET',
            })
                .then((response) => {
                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`)
                    }
                    return response.blob()
                })
                .then((blob) => {
                    const blobUrl = URL.createObjectURL(blob)
                    const link = document.createElement('a')
                    link.href = blobUrl
                    link.download = getFileName() || `document.${fileExtension || 'pdf'}`
                    document.body.appendChild(link)
                    link.click()
                    document.body.removeChild(link)
                    URL.revokeObjectURL(blobUrl)
                })
                .catch((error) => {
                    console.error('Error downloading from URL:', error)
                })
        } else {
            console.warn('No data available for download')
        }
    }, [downloadBlob, url, getFileName, fileExtension])

    useEffect(() => {
        return () => {
            if (downloadUrl) {
                URL.revokeObjectURL(downloadUrl)
            }
        }
    }, [downloadUrl])

    const isLoadingSpinner = useMemo(() => {
        return isOpen && (isBinaryLoading || isPdfLoading || isAuthLoading)
    }, [isOpen, isBinaryLoading, isPdfLoading, isAuthLoading])

    if (!previewFile) return null

    return (
        <div className={`${baseClass} ${isOpen ? `${baseClass}--open` : ''}`}>
            <div className={`${baseClass}__header`}>
                <div className={`${baseClass}__header__left`}>
                    <div className={`${baseClass}__header__left__name`}>{getFileName()}</div>
                    <div className={`${baseClass}__header__left__date`}>{authData?.created}</div>
                </div>
                <div className={`${baseClass}__header__center`}>
                    <div className={`${baseClass}__header__center__page`}>
                        <span className={`${baseClass}__page-indicator`}>
                            {currentPage} / {totalPages}
                        </span>
                    </div>
                </div>
                <div className={`${baseClass}__header__right`}>
                    <div className={`${baseClass}__zoom`}>
                        <button
                            className={`${baseClass}__button ${baseClass}__zoom__button`}
                            aria-label="zoom-decrease"
                            onClick={() => {
                                handleZoomChange('decrease')
                            }}
                        >
                            <span>–</span>
                        </button>
                        <span>{zoom}%</span>
                        <button
                            className={`${baseClass}__button ${baseClass}__zoom__button`}
                            aria-label="zoom-increase"
                            onClick={() => {
                                handleZoomChange('increase')
                            }}
                        >
                            <span>+</span>
                        </button>
                    </div>
                    <div className={`${baseClass}__header__right__nav`}>
                        {!isFitToScreen && (
                            <button
                                className={`${baseClass}__button`}
                                aria-label="expand"
                                onClick={() => {
                                    setIsFitToScreen(true)

                                    setZoom(300)
                                }}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="1em"
                                    height="1em"
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <g clipPath="url(#Icon-Expand-Regular_svg__a)">
                                        <path
                                            fillRule="evenodd"
                                            d="M14.75.85a.9.9 0 1 0 0 1.8h5.327l-6.963 6.964a.9.9 0 1 0 1.272 1.272l6.964-6.963V9.25a.9.9 0 1 0 1.8 0v-7.5a.9.9 0 0 0-.9-.9zm-5.5 22.3a.9.9 0 1 0 0-1.8H3.923l6.963-6.964a.9.9 0 1 0-1.272-1.272L2.65 20.077V14.75a.9.9 0 1 0-1.8 0v7.5a.9.9 0 0 0 .9.9z"
                                            clipRule="evenodd"
                                        ></path>
                                    </g>
                                    <defs>
                                        <clipPath id="Icon-Expand-Regular_svg__a">
                                            <path d="M0 0h24v24H0z"></path>
                                        </clipPath>
                                    </defs>
                                </svg>
                            </button>
                        )}
                        {isFitToScreen && (
                            <button
                                className={`${baseClass}__button`}
                                aria-label="expand"
                                onClick={() => {
                                    setIsFitToScreen(false)
                                    setZoom(100)
                                }}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="1em"
                                    height="1em"
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <g clipPath="url(#Icon-Collapse-Regular_svg__a)">
                                        <path
                                            fillRule="evenodd"
                                            d="M21.25 11.15a.9.9 0 1 0 0-1.8h-5.327l6.964-6.964a.9.9 0 1 0-1.273-1.272L14.65 8.077V2.75a.9.9 0 1 0-1.8 0v7.5a.9.9 0 0 0 .9.9zm-18.5 1.7a.9.9 0 1 0 0 1.8h5.327l-6.963 6.964a.9.9 0 0 0 1.272 1.273l6.964-6.964v5.327a.9.9 0 1 0 1.8 0v-7.5a.9.9 0 0 0-.9-.9z"
                                            clipRule="evenodd"
                                        ></path>
                                    </g>
                                    <defs>
                                        <clipPath id="Icon-Collapse-Regular_svg__a">
                                            <path d="M0 0h24v24H0z"></path>
                                        </clipPath>
                                    </defs>
                                </svg>
                            </button>
                        )}
                        <button
                            className={`${baseClass}__button`}
                            aria-label="print"
                            onClick={() => handlePrint(pdfBlob)}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="1em"
                                height="1em"
                                fill="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M6.94 2.9a.1.1 0 0 0-.1.1v2.975h10.32V3a.1.1 0 0 0-.1-.1zm12.02 3.075V3a1.9 1.9 0 0 0-1.9-1.9H6.94A1.9 1.9 0 0 0 5.04 3v2.975H3a1.9 1.9 0 0 0-1.9 1.9v10.168c0 1.05.85 1.9 1.9 1.9h2.04V21c0 1.05.85 1.9 1.9 1.9h10.12a1.9 1.9 0 0 0 1.9-1.9v-1.057h1.828c1.062 0 2.112-.786 2.112-1.975V7.875a1.9 1.9 0 0 0-1.9-1.9zM3 7.775a.1.1 0 0 0-.1.1v10.168a.1.1 0 0 0 .1.1h2.04V16.28c0-1.05.85-1.9 1.9-1.9h10.12c1.05 0 1.9.85 1.9 1.9v1.863h1.828c.12 0 .209-.043.26-.088.05-.044.052-.076.052-.087V7.875a.1.1 0 0 0-.1-.1zm3.94 8.405a.1.1 0 0 0-.1.1V21a.1.1 0 0 0 .1.1h10.12a.1.1 0 0 0 .1-.1v-4.72a.1.1 0 0 0-.1-.1z"
                                    clipRule="evenodd"
                                ></path>
                            </svg>
                            <span>{getTitle('1021', 'Print')}</span>
                        </button>
                        <button className={`${baseClass}__button`} aria-label="download" onClick={handleDownload}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="1em"
                                height="1em"
                                fill="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M12.9 3.5a.9.9 0 1 0-1.8 0v10.827l-2.464-2.463a.9.9 0 1 0-1.272 1.272l4 4a.9.9 0 0 0 1.272 0l4-4a.9.9 0 1 0-1.272-1.272L12.9 14.327zM2 16.6a.9.9 0 0 1 .9.9V19c0 .335.268.6.595.6h17.008a.597.597 0 0 0 .597-.6v-1.5a.9.9 0 1 1 1.8 0V19c0 1.323-1.068 2.4-2.396 2.4H3.495A2.396 2.396 0 0 1 1.1 19v-1.5a.9.9 0 0 1 .9-.9"
                                    clipRule="evenodd"
                                ></path>
                            </svg>{' '}
                            <span>{getTitle('2342', 'Download')}</span>
                        </button>
                    </div>
                </div>
                <button
                    className={`${baseClass}__button ${baseClass}__close-button`}
                    onClick={closePreview}
                    aria-label="Close preview"
                >
                    <span>✕</span>
                </button>
            </div>
            <div
                className={`${baseClass}__content`}
                ref={containerRef}
                onScroll={() => {
                    updateCurrentPage()
                }}
            >
                {isLoadingSpinner && <div className={`${baseClass}__content__spinner`} />}

                <div className={`${baseClass}__pages`}>
                    {pdfDoc &&
                        Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                            <div
                                key={pageNum}
                                className={`${baseClass}__page ${currentPage === pageNum ? `${baseClass}__page--active` : ''}`}
                                ref={(el) => {
                                    pageRefs.current[pageNum - 1] = el
                                }}
                            >
                                {/*<div className={`${baseClass}__page-number`}>Page {pageNum}</div>*/}
                                <div
                                    className={`${baseClass}__page-container`}
                                    style={{
                                        backgroundColor: '#ffffff',
                                        borderRadius: '4px',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                    }}
                                >
                                    <canvas
                                        ref={(el) => {
                                            canvasRefs.current[pageNum - 1] = el
                                        }}
                                        data-page-num={pageNum}
                                        style={{
                                            display: 'block',
                                            width: '100%',
                                            height: 'auto',
                                            borderRadius: '4px',
                                        }}
                                    />
                                </div>
                                {!renderedPages.current.has(pageNum) && (
                                    <div className={`${baseClass}__page-loader`}>Loading page {pageNum}...</div>
                                )}
                            </div>
                        ))}
                </div>
            </div>
        </div>
    )
}

export default FilePdfPreview
